import { FC, useRef, useState, JSX } from 'react';
import {
  FoldingEdgeConverter,
  EdgeStyleIndicatorRenderer,
  GraphComponent,
  GraphViewerInputMode,
  IGraph,
  LayoutOrientation,
  NodeStyleIndicatorRenderer,
  PolylineEdgeStyle,
  StyleIndicatorZoomPolicy,
} from '@ardoq/yfiles';
import styled from 'styled-components';
import { GraphWrapper } from './GraphWrapper';
import {
  OnItemDoubleClick,
  OnItemFocus,
  SimpleBlockDiagramEdge,
  SimpleBlockDiagramLabels,
  SimpleBlockDiagramNode,
} from './types';
import {
  createCollapsibleGraphViewerInputMode,
  createCollapsibleNodeStyleDecorator,
  createDefaultEdgeLabelLayoutParameter,
  createDefaultEdgeStyle,
  createDefaultFoldingManager,
  createDefaultLabelStyle,
  createDefaultNodeStyle,
  getHighlightedEdgeStyle,
  getHighlightedNodeStyle,
} from './utils';
import { EDGE_LABEL_CONTAINER_CLASS_NAME, NodeDefaults } from './consts';
import { useEffectOnce } from '@ardoq/hooks';
import { SIMPLE_BLOCK_DIAGRAM_COLLAPSE_BUTTON } from '@ardoq/graph';
import { colors } from '@ardoq/design-tokens';
import { GroupingCollapseState } from '@ardoq/api-types';

const Wrapper = styled(GraphWrapper)`
  width: 100%;
  height: 100%;
  .${SIMPLE_BLOCK_DIAGRAM_COLLAPSE_BUTTON} {
    cursor: pointer;
  }
  .${EDGE_LABEL_CONTAINER_CLASS_NAME} {
    stroke: ${colors.white950};
    stroke-width: 3px;
    stroke-opacity: 0.8;
    paint-order: stroke;
  }
`;

export type SimpleBlockDiagramProps = {
  nodes: SimpleBlockDiagramNode[];
  edges: SimpleBlockDiagramEdge[];
  className?: string;
  layoutOrientation: LayoutOrientation;
  defaultGroupingCollapseState: GroupingCollapseState;
  edgeGrouping?: boolean;
  focusedItemId?: string;
  centeredItemId?: string;
  loadingAsset?: JSX.Element | null;
  onItemFocus?: OnItemFocus;
  onItemDoubleClick?: OnItemDoubleClick;
  onCanvasClick?: VoidFunction;
  /**
   * `registerVisualizationContainer` provides a way to access the visualization
   * container element from outside the `SimpleBlockDiagram`
   * component. Is is, however, not necessary for rendering the visualization.
   */
  registerVisualizationContainer?: (container: HTMLElement) => void;
  unregisterVisualizationContainer?: () => void;
  /**
   * `registerGraphComponent` provides a way to access the yFiles graph
   * component from outside the `SimpleBlockDiagram` component. It
   * is, however, not necessary for rendering the visualization.
   */
  registerGraphComponent?: (graphComponent: GraphComponent) => void;
  unregisterGraphComponent?: () => void;
  animateLayout?: boolean;
  hasZoomControls?: boolean;
  lightenDarkColors?: boolean;
  showLeftMenu?: boolean;
  mergeSameEdges?: boolean;
  /**
   * Optional translated strings for built-in UI affordances (collapse/expand
   * all controls, zoom in/out tooltips). Each label has an English default,
   * so passing `undefined` (or omitting individual labels) preserves the
   * previous behaviour.
   */
  labels?: Partial<SimpleBlockDiagramLabels>;
};

export const SimpleBlockDiagram: FC<SimpleBlockDiagramProps> = ({
  nodes,
  edges,
  className,
  layoutOrientation,
  defaultGroupingCollapseState,
  edgeGrouping,
  focusedItemId,
  centeredItemId,
  loadingAsset = null,
  onItemFocus,
  onItemDoubleClick,
  onCanvasClick,
  registerVisualizationContainer,
  unregisterVisualizationContainer,
  registerGraphComponent,
  unregisterGraphComponent,
  animateLayout,
  hasZoomControls,
  lightenDarkColors = true,
  showLeftMenu,
  mergeSameEdges,
  labels,
}) => {
  const graphRef = useRef<IGraph | null>(null);
  const inputModeRef = useRef<GraphViewerInputMode | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffectOnce(() => {
    const inputMode = createCollapsibleGraphViewerInputMode();
    const manager = createDefaultFoldingManager();
    manager.foldingEdgeConverter = new FoldingEdgeConverter({
      foldingEdgeDefaults: { copyLabels: true },
    });
    const graph = manager.createFoldingView().graph;
    graph.nodeDefaults.style = createDefaultNodeStyle();
    graph.edgeDefaults.style = createDefaultEdgeStyle();
    graph.edgeDefaults.labels.layoutParameter =
      createDefaultEdgeLabelLayoutParameter();

    graph.decorator.nodes.highlightRenderer.addFactory(node => {
      if (!node) return null;
      return new NodeStyleIndicatorRenderer({
        zoomPolicy: StyleIndicatorZoomPolicy.WORLD_COORDINATES,
        nodeStyle: getHighlightedNodeStyle(node),
        margins: NodeDefaults.DECORATION_MARGINS,
      });
    });
    graph.decorator.nodes.selectionRenderer.addFactory(node => {
      if (!node) return null;
      return new NodeStyleIndicatorRenderer({
        zoomPolicy: StyleIndicatorZoomPolicy.WORLD_COORDINATES,
        nodeStyle: getHighlightedNodeStyle(node),
        margins: NodeDefaults.DECORATION_MARGINS,
      });
    });
    graph.decorator.edges.highlightRenderer.addFactory(edge => {
      if (!edge) return null;
      const edgeStyle = edge.style as PolylineEdgeStyle;
      return new EdgeStyleIndicatorRenderer({
        zoomPolicy: StyleIndicatorZoomPolicy.WORLD_COORDINATES,
        edgeStyle: getHighlightedEdgeStyle(edgeStyle),
      });
    });
    graph.decorator.edges.selectionRenderer.addFactory(edge => {
      if (!edge) return null;
      const edgeStyle = edge.style as PolylineEdgeStyle;
      return new EdgeStyleIndicatorRenderer({
        zoomPolicy: StyleIndicatorZoomPolicy.WORLD_COORDINATES,
        edgeStyle: getHighlightedEdgeStyle(edgeStyle),
      });
    });
    graph.decorator.edges.focusRenderer.hide();
    graph.decorator.nodes.focusRenderer.hide();
    graph.decorator.labels.highlightRenderer.hide();
    graph.decorator.labels.selectionRenderer.hide();
    graph.groupNodeDefaults.labels.style = createDefaultLabelStyle();

    const foldingView = graph.foldingView;
    if (foldingView !== null) {
      foldingView.graph.groupNodeDefaults.style =
        createCollapsibleNodeStyleDecorator(foldingView);
    }

    graphRef.current = graph;
    inputModeRef.current = inputMode;
    setIsLoaded(true);
  });

  return isLoaded ? (
    <Wrapper
      graph={graphRef.current}
      nodes={nodes}
      edges={edges}
      inputMode={inputModeRef.current}
      className={className}
      onItemFocus={onItemFocus}
      onItemDoubleClick={onItemDoubleClick}
      onCanvasClick={onCanvasClick}
      registerVisualizationContainer={registerVisualizationContainer}
      unregisterVisualizationContainer={unregisterVisualizationContainer}
      registerGraphComponent={registerGraphComponent}
      unregisterGraphComponent={unregisterGraphComponent}
      animateLayout={animateLayout}
      layoutOrientation={layoutOrientation}
      defaultGroupingCollapseState={defaultGroupingCollapseState}
      edgeGrouping={Boolean(edgeGrouping)}
      focusedItemId={focusedItemId}
      centeredItemId={centeredItemId}
      hasZoomControls={hasZoomControls}
      lightenDarkColors={lightenDarkColors}
      showLeftMenu={showLeftMenu}
      mergeSameEdges={mergeSameEdges}
      labels={labels}
    />
  ) : (
    loadingAsset
  );
};
